import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, Minus, Plus, Truck, RotateCcw, Sparkles, ChevronRight } from "lucide-react";
import { api, gbp, formatApiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, wishlist, toggleWishlist, user } = useStore();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [sel, setSel] = useState({});
  const [qty, setQty] = useState(1);
  const [reviewForm, setReviewForm] = useState({ author: "", rating: 5, title: "", body: "" });

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    api.get(`/shopify/product/${slug}`).then(async (r) => {
      const p = r.data; setProduct(p); setActiveImg(0);
      api.post("/track", { path: `/product/${slug}`, title: p.title, type: "product", product_id: p.id, product_title: p.title }).catch(() => {});
      setSel(Object.fromEntries((p.options || []).map((o) => [o.position, o.values[0]])));
      api.get(`/reviews?product_id=${p.id}`).then((rr) => setReviews(rr.data)).catch(() => {});
      api.get(`/shopify/products`).then((rr) => setRelated(rr.data.filter((x) => x.id !== p.id).slice(0, 4))).catch(() => setRelated([]));
    }).catch(() => navigate("/shop"));
  }, [slug, navigate]);

  if (!product) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-muted-foreground">Loading…</div>;

  const options = product.options || [];
  const isColourOpt = (name) => /colou?r|pom/i.test(name || "");
  const SWATCH = { white: "#f5f5f5", black: "#1f1f1f", brown: "#8a6a4f", grey: "#9a9a9a", gray: "#9a9a9a", cream: "#eee3d0", natural: "#e8d9c5", oatmeal: "#e8d9c5", pink: "#deb5c0", blush: "#deb5c0", navy: "#2c3e50", tweed: "#b5a48c", wheat: "#e3c98f", charcoal: "#3a3a3a", sprinkles: "#deb5c0" };
  const swatchFor = (n) => SWATCH[(n || "").toLowerCase().trim()] || (product.colours || []).find((c) => c.name.toLowerCase() === (n || "").toLowerCase())?.swatch || "#deb5c0";
  const slug2 = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const variantFor = (s) => (product.variants || []).find((v) => options.every((o) => v[`o${o.position}`] === s[o.position]));
  const curVariant = variantFor(sel);
  const choose = (pos, val) => { setSel((s) => ({ ...s, [pos]: val })); setActiveImg(0); };
  const selLabel = options.map((o) => sel[o.position]).filter(Boolean).join(" / ");

  const basePrice = product.sale_price || product.price;
  const unitPrice = basePrice;
  const onSale = product.sale_price && product.sale_price < product.price;
  const variantImage = curVariant?.image;
  const images = variantImage ? [variantImage, ...product.images.filter((i) => i !== variantImage)] : product.images;
  const available = curVariant ? curVariant.available : product.stock > 0;
  const soldOut = !available;
  const fav = wishlist.includes(product.id);
  const avgRating = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;
  const variantId = () => curVariant?.id || product.default_variant_id;

  const buildItem = () => ({
    type: "product", id: product.id, title: product.title, qty,
    unit_price: Number(unitPrice.toFixed(2)), colour: selLabel || null, image: images[0],
    variant_id: variantId(),
  });
  const add = () => { if (soldOut) return; addToCart(buildItem()); };
  const buyNow = async () => {
    if (soldOut) return;
    try {
      const { data } = await api.post("/shopify/checkout", { lines: [{ variant_id: variantId(), quantity: qty }] });
      window.location.href = data.url;
    } catch { toast.error("Could not start checkout. Please try again."); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", { ...reviewForm, product_id: product.id });
      toast.success("Thank you! Your review will appear once approved.");
      setReviewForm({ author: "", rating: 5, title: "", body: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const productSchema = {
    "@context": "https://schema.org", "@type": "Product", name: product.title,
    description: product.meta_description || product.description, image: product.images,
    brand: { "@type": "Brand", name: "Crochet Kerry" },
    offers: { "@type": "Offer", priceCurrency: "GBP", price: unitPrice.toFixed(2),
      availability: soldOut ? "https://schema.org/OutOfStock" : "https://schema.org/InStock" },
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Seo title={product.seo_title || product.title} description={product.meta_description} image={product.images?.[0]} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />

      <nav className="flex items-center gap-1 text-xs text-muted-foreground mb-8">
        <Link to="/" className="hover:text-brand-ink">Home</Link><ChevronRight className="w-3 h-3" />
        <Link to={`/shop?category=${encodeURIComponent(product.category)}`} className="hover:text-brand-ink">{product.category}</Link><ChevronRight className="w-3 h-3" />
        <span className="text-brand-ink">{product.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        {/* gallery */}
        <div>
          <div className="aspect-[4/5] overflow-hidden rounded-sm bg-brand-blush/40 mb-3">
            <img src={images[activeImg]} alt={product.alt_text || product.title} className="w-full h-full object-cover" data-testid="product-main-image" />
          </div>
          {images.length > 1 && (
            <div className="flex gap-3">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-24 overflow-hidden rounded-sm border-2 ${activeImg === i ? "border-brand-ink" : "border-transparent"}`}>
                  <img src={img} alt={`${product.title} ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* info */}
        <div>
          <p className="text-xs uppercase tracking-luxe text-brand-pink mb-2">{product.subcategory}</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl leading-tight mb-3" data-testid="product-title">{product.title}</h1>
          {avgRating && <p className="text-sm text-brand-pink mb-3">{"★".repeat(Math.round(avgRating))} <span className="text-muted-foreground">{avgRating} ({reviews.length})</span></p>}
          <div className="flex items-center gap-3 mb-5">
            {onSale
              ? <><span className="text-2xl text-brand-pink font-medium" data-testid="product-price">{gbp(unitPrice)}</span><span className="text-lg text-muted-foreground line-through">{gbp(product.price)}</span></>
              : <span className="text-2xl font-medium" data-testid="product-price">{gbp(unitPrice)}</span>}
          </div>
          {product.description_html
            ? <div className="ck-desc text-muted-foreground leading-relaxed mb-6" data-testid="product-description" dangerouslySetInnerHTML={{ __html: product.description_html }} />
            : <p className="text-muted-foreground leading-relaxed mb-6" data-testid="product-description">{product.description}</p>}

          {/* variant options */}
          {options.map((o) => (
            <div className="mb-6" key={o.position}>
              <p className="text-xs uppercase tracking-luxe mb-3">{o.name}: <span className="text-muted-foreground">{sel[o.position]}</span></p>
              <div className="flex flex-wrap gap-3">
                {o.values.map((val) => {
                  const active = sel[o.position] === val;
                  return isColourOpt(o.name) ? (
                    <button key={val} onClick={() => choose(o.position, val)} title={val}
                      className={`w-10 h-10 rounded-full border-2 transition-all ${active ? "border-brand-ink scale-110" : "border-brand-border"}`}
                      style={{ background: swatchFor(val) }} data-testid={`opt-${o.position}-${slug2(val)}`} />
                  ) : (
                    <button key={val} onClick={() => choose(o.position, val)}
                      className={`px-4 py-2 rounded-sm border text-sm transition-colors ${active ? "border-brand-ink bg-brand-blush" : "border-brand-border hover:bg-brand-blush/50"}`}
                      data-testid={`opt-${o.position}-${slug2(val)}`}>{val}</button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* stock */}
          <p className="text-sm mb-5" data-testid="stock-status">
            {soldOut ? <span className="text-destructive">● Sold out</span>
              : <span className="text-[#2D6A4F]">● In stock</span>}
          </p>

          {/* qty + buttons */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center border border-brand-border rounded-sm">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-3 hover:bg-brand-blush" data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
              <span className="px-4 text-sm w-10 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-3 py-3 hover:bg-brand-blush" data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={() => toggleWishlist(product.id)} className="w-12 h-12 border border-brand-border rounded-sm flex items-center justify-center hover:bg-brand-blush transition-colors" data-testid="product-wishlist"><Heart className={`w-5 h-5 ${fav ? "fill-brand-ink" : ""}`} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            <button onClick={add} disabled={soldOut} className="bg-brand-ink text-white py-3.5 rounded-sm text-sm uppercase tracking-luxe hover:bg-brand-pink hover:text-brand-ink transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="add-to-cart-button">Add to basket</button>
            <button onClick={buyNow} disabled={soldOut} className="border border-brand-ink py-3.5 rounded-sm text-sm uppercase tracking-luxe hover:bg-brand-blush transition-colors disabled:opacity-40" data-testid="buy-now-button">Buy it now</button>
          </div>

          {/* delivery facts */}
          <div className="grid grid-cols-1 gap-3 text-sm border-y border-brand-border py-5 mb-6">
            <div className="flex items-center gap-3"><Sparkles className="w-4 h-4 text-brand-pink" /> Processing: {product.processing_time}</div>
            <div className="flex items-center gap-3"><Truck className="w-4 h-4 text-brand-pink" /> Delivery: {product.delivery_estimate} (costs shown at checkout)</div>
            <div className="flex items-center gap-3"><RotateCcw className="w-4 h-4 text-brand-pink" /> {product.returns_note}</div>
          </div>

          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="materials"><AccordionTrigger className="text-sm uppercase tracking-luxe">Materials</AccordionTrigger><AccordionContent className="text-sm text-muted-foreground">{product.materials}</AccordionContent></AccordionItem>
            <AccordionItem value="size"><AccordionTrigger className="text-sm uppercase tracking-luxe">Size</AccordionTrigger><AccordionContent className="text-sm text-muted-foreground whitespace-pre-line">{product.size}</AccordionContent></AccordionItem>
            <AccordionItem value="care"><AccordionTrigger className="text-sm uppercase tracking-luxe">Care instructions</AccordionTrigger><AccordionContent className="text-sm text-muted-foreground">{product.care_instructions}</AccordionContent></AccordionItem>
            <AccordionItem value="returns"><AccordionTrigger className="text-sm uppercase tracking-luxe">Delivery & returns</AccordionTrigger><AccordionContent className="text-sm text-muted-foreground">{product.returns_note} See our <Link to="/delivery-returns" className="underline">Delivery & Returns</Link> page for full details.</AccordionContent></AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* reviews */}
      <section className="mt-20 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12" data-testid="reviews-section">
        <div>
          <h2 className="font-serif-display text-3xl mb-6">Reviews{reviews.length ? ` (${reviews.length})` : ""}</h2>
          {reviews.length === 0 ? <p className="text-muted-foreground text-sm">No reviews yet — be the first to share your thoughts.</p> : (
            <div className="space-y-6">
              {reviews.map((r) => (
                <div key={r.id} className="border-b border-brand-border pb-5">
                  <div className="text-brand-pink text-sm">{"★".repeat(r.rating)}<span className="text-brand-border">{"★".repeat(5 - r.rating)}</span></div>
                  <h4 className="font-serif-display text-xl mt-1">{r.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{r.body}</p>
                  <p className="text-xs uppercase tracking-luxe mt-2">— {r.author}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-brand-blush/40 rounded-sm p-7 h-fit">
          <h3 className="font-serif-display text-2xl mb-4">Write a review</h3>
          <form onSubmit={submitReview} className="space-y-3">
            <input required placeholder="Your name" value={reviewForm.author} onChange={(e) => setReviewForm({ ...reviewForm, author: e.target.value })} className="w-full border border-brand-border rounded-sm px-3 py-2 text-sm bg-white outline-none focus:border-brand-pink" data-testid="review-author" />
            <select value={reviewForm.rating} onChange={(e) => setReviewForm({ ...reviewForm, rating: Number(e.target.value) })} className="w-full border border-brand-border rounded-sm px-3 py-2 text-sm bg-white" data-testid="review-rating">
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>)}
            </select>
            <input placeholder="Review title" value={reviewForm.title} onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })} className="w-full border border-brand-border rounded-sm px-3 py-2 text-sm bg-white outline-none focus:border-brand-pink" data-testid="review-title" />
            <textarea required placeholder="Your review" rows={3} value={reviewForm.body} onChange={(e) => setReviewForm({ ...reviewForm, body: e.target.value })} className="w-full border border-brand-border rounded-sm px-3 py-2 text-sm bg-white outline-none focus:border-brand-pink" data-testid="review-body" />
            <button className="w-full bg-brand-ink text-white py-2.5 rounded-sm text-sm uppercase tracking-luxe hover:bg-brand-pink hover:text-brand-ink transition-colors" data-testid="review-submit">Submit review</button>
          </form>
        </div>
      </section>


      {related.length > 0 && (
        <section className="mt-20">
          <h2 className="font-serif-display text-3xl mb-8">You may also like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">{related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
        </section>
      )}
    </div>
  );
}
